import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@core/button";
import { Home, RefreshCw } from "lucide-react";
import { getEnvironment } from "@packages/utils";
import axios, { AxiosError } from "axios";
import logo from "@packages/assets/logo.png";
import { useTranslation } from "react-i18next";

const Maintenance = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const { t } = useTranslation();

  const checkApiStatus = async () => {
    setIsChecking(true);
    try {
      const baseUrl =
        getEnvironment() === "DEV"
          ? "https://api-dev.madlen.io/api"
          : "https://api.madlen.io/api";

      await axios.get(`${baseUrl}/health`);
      // If the request succeeds, redirect to home
      window.location.href = "/";
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 502) {
        // Still under maintenance
        setIsChecking(false);
      } else {
        // Other error occurred, redirect to home
        window.location.href = "/";
      }
    }
  };

  useEffect(() => {
    // Check API status when component mounts
    // checkApiStatus();
  }, []);

  const handleRefresh = () => {
    checkApiStatus();
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-orange-100 dark:from-gray-900 dark:to-orange-950 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Top decorative bar */}
          <div className="h-2 bg-gradient-to-r from-orange-200 via-orange-500 to-orange-200"></div>

          <div className="p-8 md:p-12">
            {/* Maintenance icon with pulsing effect */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500/20 dark:bg-orange-500/10 rounded-full blur-xl animate-pulse"></div>
                <div className="relative bg-orange-100 dark:bg-orange-900/50 p-6 rounded-full">
                  <img src={logo} alt="Logo" width={48} height={48} />
                </div>
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-800 dark:text-white mb-4">
              {t("maintenance.title")}
            </h1>

            <p className="text-lg text-gray-600 dark:text-gray-300 text-center mb-8">
              {t("maintenance.description")}
            </p>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleGoHome}
                variant="solid"
                size="lg"
                rounded="xl"
                bgColor="bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-800"
                textColor="text-white"
                className="shadow-lg hover:shadow-xl transition-all duration-300"
                leftIcon={<Home size={20} />}
              >
                {t("maintenance.returnToHomepage")}
              </Button>

              <Button
                onClick={handleRefresh}
                variant="outline"
                size="lg"
                rounded="xl"
                borderColor="border-orange-300 dark:border-orange-700"
                textColor="text-orange-700 dark:text-orange-300"
                className="hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-all duration-300"
                leftIcon={
                  <RefreshCw
                    size={20}
                    className={
                      isChecking ? "animate-spin" : "animate-spin-slow"
                    }
                  />
                }
                loading={isChecking}
                disabled={isChecking}
              >
                {t("maintenance.tryAgain")}
              </Button>
            </div>
          </div>
        </div>

        {/* Additional information */}
        <div className="mt-6 text-center text-gray-600 dark:text-gray-400 text-sm">
          {t("maintenance.contactSupport")}
          <a
            href="mailto:support@madlen.io"
            className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 ml-2"
          >
            support@madlen.io
          </a>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
